import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, CheckCircle, ArrowRight } from "lucide-react";
import { useLang } from "../LangContext";
import Breadcrumb from "../Breadcrumb";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/rb17en5i_logo_prodimmo-removebg-preview.png";

export default function PhotoImmobilier() {
  const { t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    "Photos HDR haute résolution intérieures et extérieures",
    "Retouches professionnelles et correction de perspective",
    "Mise en scène et optimisation de la lumière naturelle",
    "Photos twilight (crépuscule) pour un rendu premium",
    "Formats optimisés web, print et portails immobiliers",
    "Livraison sous 48 heures",
  ];

  const images = [
    "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/vudiz985__DSC1271-HDR-Enhanced-NR-Edit.jpg",
    "/fromagerie_marsens.jpg",
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Photographe Immobilier Professionnel | Prodimmo , Suisse</title>
        <meta name="description" content="Photographe immobilier professionnel en Suisse romande. Photos HDR, retouches pro, livraison 48h. Les biens avec photos pro se vendent 32% plus vite." />
        <meta property="og:title" content="Photographe Immobilier Professionnel | Prodimmo , Suisse" />
        <meta property="og:description" content="Photos immobilières professionnelles HDR. Vos biens se vendent 32% plus vite avec des photos de qualité." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.prodimmo.ch/photo-immobilier" />
      </Helmet>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Prodimmo" className="h-9" />
            <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Prodimmo</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#73B5E2] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[
            { label: "Prestations", href: "/#prestations" },
            { label: "Photo Immobilière" },
          ]} />
        </div>
      </nav>

      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">Photo immobilière</span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit' }}>
              Photographe immobilier professionnel<br />
              <span className="text-[#73B5E2]">en Suisse romande</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg max-w-2xl leading-relaxed">
              Des photos immobilières de qualité professionnelle sont essentielles pour attirer les acheteurs. 
              Notre expertise en photographie immobilière met en valeur chaque espace avec un éclairage optimal 
              et des retouches soignées pour un résultat qui fait la différence.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <div className="grid grid-cols-2 gap-4">
              {images.map((img, i) => (
                <div key={i} className="rounded-2xl overflow-hidden aspect-square">
                  <img src={img} alt={`Photo immobilière ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-3 text-center">Exemples de photos , Gex et Dorthe</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
              Notre service photo immobilière
            </h2>
            <ul className="space-y-4">
              {features.map((f, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.08 }} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#73B5E2] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{f}</span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-8 flex gap-4">
              <Link to="/#contact" className="bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-6 py-3 text-sm font-medium transition-all hover:scale-105 shadow-lg shadow-blue-500/20">
                Réserver un shooting
              </Link>
              <Link to="/#tarifs" className="border border-slate-200 text-slate-700 hover:border-[#73B5E2] rounded-full px-6 py-3 text-sm font-medium transition-all">
                Voir les tarifs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            L'impact de la photo professionnelle
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            Les biens présentés avec des photos professionnelles se vendent 32% plus vite et à un prix 
            supérieur de 5 à 10% par rapport aux annonces avec des photos amateur. L'investissement dans 
            un photographe immobilier se rentabilise dès la première vente.
          </p>
          <div className="grid grid-cols-3 gap-6">
            {[{ val: "32%", desc: "Vente plus rapide" }, { val: "+10%", desc: "Prix de vente supérieur" }, { val: "95%", desc: "Acheteurs cherchent en ligne" }].map((s, i) => (
              <div key={i}>
                <p className="text-3xl font-bold text-[#73B5E2]" style={{ fontFamily: 'Outfit' }}>{s.val}</p>
                <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center gap-4">
          <Link to="/video-immobilier" className="flex items-center gap-2 text-[#73B5E2] hover:text-[#5AA0D0] font-medium text-sm transition-colors">
            Vidéo immobilière <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/drone-fpv-immobilier" className="flex items-center gap-2 text-[#73B5E2] hover:text-[#5AA0D0] font-medium text-sm transition-colors">
            Drone FPV <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
